import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { ChevronRightIcon, CheckIcon, EraserIcon, LockIcon, LoaderIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
import { SuccessModal } from './SuccessModal';
import { useClient } from './ClientContext';
/*
 * WEBHOOK INTEGRATION
 * -------------------
 * The webhook endpoint below should handle creating a Notion page
 * using the Notion API with the received form data.
 *
 * Expected POST body:
 * {
 *   clientName: string,
 *   role: string,
 *   company: string,
 *   date: string,
 *   signature: boolean,
 *   agreedToTerms: boolean,
 *   projectName: string
 * }
 *
 * The webhook server should:
 * 1. Receive the form data
 * 2. Use the Notion API (https://developers.notion.com/) to create
 *    a new page in your project database with the client details
 * 3. Return a 200 status on success
 *
 * Replace the placeholder URL with your actual webhook endpoint
 * (e.g. a Zapier, Make.com, or custom serverless function URL).
 */
const WEBHOOK_URL = 'https://your-webhook-url.com/approve';
export function SignatureSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [approved, setApproved] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    clientName,
    setClientName,
    companyName,
    setCompanyName
  } = useClient();
  const [formData, setFormData] = useState({
    name: clientName,
    role: '',
    company: companyName,
    date: new Date().toLocaleDateString('en-GB')
  });
  // Sync form data to context
  useEffect(() => {
    setClientName(formData.name);
  }, [formData.name, setClientName]);
  useEffect(() => {
    setCompanyName(formData.company);
  }, [formData.company, setCompanyName]);
  // Check if all requirements are met for the approve slider
  const canApprove = hasSignature && agreed && formData.name.trim() !== '' && formData.role.trim() !== '' && formData.company.trim() !== '';
  // Canvas setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = 80;
        ctx.strokeStyle = '#0A1628';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
      }
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);
  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : (e as React.MouseEvent).nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : (e as React.MouseEvent).nativeEvent.offsetY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : (e as React.MouseEvent).nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : (e as React.MouseEvent).nativeEvent.offsetY;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };
  const stopDrawing = () => setIsDrawing(false);
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setApproved(false);
  };
  const x = useMotionValue(0);
  const xInput = [0, 220];
  const backgroundOpacity = useTransform(x, xInput, [0, 1]);
  const handleDragEnd = () => {
    if (!canApprove) {
      return;
    }
    if (x.get() > 180) {
      setApproved(true);
    } else {
      setApproved(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!approved || !agreed || !hasSignature || !canApprove || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);
    const payload = {
      clientName: formData.name,
      role: formData.role,
      company: formData.company,
      date: formData.date,
      signature: true,
      agreedToTerms: true,
      projectName: `Brand Identity Design (MVB) — ${formData.company}`
    };
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(`Submission failed (${response.status}). Please try again.`);
      }
      setShowModal(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="sign" className="py-24 px-6 bg-offwhite" data-id="element-154">
      <SuccessModal isOpen={showModal} onClose={() => setShowModal(false)} data-id="element-155" />

      <div className="max-w-7xl mx-auto" data-id="element-156">
        <ScrollReveal data-id="element-157">
          <div className="mb-16" data-id="element-158">
            <span className="text-accent font-bold tracking-wider text-sm" data-id="element-159">
              Agreement
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-navy leading-tight mt-2" data-id="element-160">
              Let's create something
              <br data-id="element-161" />
              great together.
            </h2>
            <div className="mt-8 max-w-2xl" data-id="element-162">
              <p className="text-gray-600 text-lg leading-relaxed" data-id="element-163">
                If you're happy to move forward, please sign the proposal below.
                This enables us to secure resources and begin onboarding. A
                deposit invoice will follow, and work will commence once payment
                is received.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mt-4" data-id="element-164">
                We look forward to collaborating.
              </p>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={0.2} data-id="element-165">
          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-3xl shadow-xl max-w-3xl mx-auto" data-id="element-166">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-167">
              <div data-id="element-168">
                <label className="block text-xs font-bold text-gray-400 mb-2" data-id="element-169">
                  Client name
                </label>
                <input type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-navy font-bold focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all" data-id="element-170" />
              </div>
              <div data-id="element-171">
                <label className="block text-xs font-bold text-gray-400 mb-2" data-id="element-172">
                  Role
                </label>
                <input type="text" placeholder="e.g. Founder" required value={formData.role} onChange={e => setFormData({
                ...formData,
                role: e.target.value
              })} className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-navy font-bold focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all" data-id="element-173" />
              </div>
              <div data-id="element-174">
                <label className="block text-xs font-bold text-gray-400 mb-2" data-id="element-175">
                  Company
                </label>
                <input type="text" value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-navy font-bold focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all" data-id="element-176" />
              </div>
              <div data-id="element-177">
                <label className="block text-xs font-bold text-gray-400 mb-2" data-id="element-178">
                  Date
                </label>
                <input type="text" value={formData.date} readOnly className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-500 cursor-not-allowed font-medium" data-id="element-179" />
              </div>
            </div>

            <div className="mb-12" data-id="element-180">
              <label className="flex items-center cursor-pointer group p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors" data-id="element-181">
                <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors mr-4 ${agreed ? 'bg-accent border-accent' : 'border-gray-300 group-hover:border-accent'}`} data-id="element-182">
                  {agreed && <CheckIcon className="w-4 h-4 text-white" data-id="element-183" />}
                </div>
                <input type="checkbox" checked={agreed} onChange={e => {
                setAgreed(e.target.checked);
              }} className="hidden" data-id="element-184" />
                <span className="text-sm text-gray-600 font-medium" data-id="element-185">
                  I agree to the terms and conditions outlined above
                </span>
              </label>
            </div>

            {/* Signature and Slide to Approve */}
            <div className="flex flex-col md:flex-row gap-6 items-start mb-8" data-id="element-186">
              {/* Signature Canvas */}
              <div className="flex-1 w-full md:w-auto" data-id="element-187">
                <label className="block text-xs font-bold text-gray-400 mb-2" data-id="element-188">
                  Signature
                </label>
                <div className="relative border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 overflow-hidden touch-none" data-id="element-189">
                  <canvas ref={canvasRef} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} className="w-full cursor-crosshair" style={{
                  height: '80px'
                }} data-id="element-190" />
                  {!hasSignature && !isDrawing && <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-gray-400 text-sm" data-id="element-191">
                      Sign here
                    </div>}
                </div>
                <button type="button" onClick={clearSignature} className="mt-2 text-xs text-gray-500 hover:text-red-500 flex items-center transition-colors" data-id="element-192">
                  <EraserIcon className="w-3 h-3 mr-1" data-id="element-193" /> Clear
                </button>
              </div>

              {/* Slide to Approve */}
              <div className="flex-shrink-0" data-id="element-194">
                <label className="block text-xs font-bold text-gray-400 mb-2" data-id="element-195">
                  Approve
                </label>
                <div className={`relative w-[280px] h-[64px] rounded-full overflow-hidden flex items-center shadow-lg transition-opacity duration-300 ${canApprove ? 'bg-black' : 'bg-gray-400 cursor-not-allowed'}`} data-id="element-196">
                  <motion.div className="absolute inset-0 bg-green-500" style={{
                  opacity: approved ? 1 : canApprove ? backgroundOpacity : 0
                }} data-id="element-197" />

                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-id="element-198">
                    {!canApprove && !approved ? <span className="text-white/70 font-medium tracking-wide text-sm flex items-center gap-2" data-id="element-199">
                        <LockIcon className="w-4 h-4" data-id="element-200" />
                        Complete all fields
                      </span> : <span className="text-white font-bold tracking-wide" data-id="element-201">
                        {approved ? 'Approved ✓' : 'Slide to approve'}
                      </span>}
                  </div>

                  {canApprove && <motion.div className="w-[56px] h-[56px] bg-white rounded-full absolute left-1 flex items-center justify-center shadow-md z-20 cursor-grab active:cursor-grabbing" drag="x" dragConstraints={{
                  left: 0,
                  right: 220
                }} dragElastic={0.1} dragSnapToOrigin={!approved} onDragEnd={handleDragEnd} style={{
                  x: approved ? 220 : x
                }} data-id="element-202">
                      {approved ? <CheckIcon className="w-6 h-6 text-green-500" data-id="element-203" /> : <ChevronRightIcon className="w-6 h-6 text-black" data-id="element-204" />}
                    </motion.div>}
                </div>
                {!canApprove && <p className="text-xs text-gray-400 mt-2 max-w-[280px]" data-id="element-205">
                    Fill all fields, sign, and agree to terms first
                  </p>}
              </div>
            </div>

            {/* Submit button with loading state */}
            <div className={`transition-all duration-500 overflow-hidden ${approved && agreed && formData.role && hasSignature ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-206">
              <button type="submit" disabled={isSubmitting} className={`w-full py-4 font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 ${isSubmitting ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-accent text-white hover:bg-accent-hover'}`} data-id="element-207">
                {isSubmitting ? <>
                    <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-208" />
                    Submitting...
                  </> : 'Submit proposal'}
              </button>

              {/* Error message */}
              <AnimatePresence data-id="element-209">
                {submitError && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center font-medium" data-id="element-210">
                    {submitError}
                  </motion.div>}
              </AnimatePresence>
            </div>

            {!canApprove && <p className="text-center text-xs text-gray-400 mt-4" data-id="element-211">
                Please fill all fields, sign, agree to terms, and slide to
                approve to submit.
              </p>}
          </form>
        </ScrollReveal>
      </div>
    </section>;
}